import React, { useEffect } from "react";
import DatalakeClipPublicationInfo from "./DatalakeClipPublicationInfo";
import ShowClipIdHeader from "./ShowClipIdHeader";
import DatalakeMetadataView from "./DatalakeMetadataView";
import DetailViewFooter from "./DetailViewFooter";
import DatalakeHeaderActions from "./DatalakeHeaderActions";
import DatalakeGalleryCarousel from "./DatalakeGalleryCarousel";
import Shortcutter from "libraries/shortcutter";
import session from "models/sessionInstance";
import highlight from "libraries/highlight";
import { adminClipUrl } from '../config/backend';
import highlightRichText from "libraries/highlightRichText";
import Analytics from "libraries/analytics";
import AgentResultDownload from "models/agentResultDownload";

interface Props {
  selectedAgentResults: any; // Backbone.Model (SelectedAgentResults)
  agentResult: any; // Backbone.Model (AgentResult)
  readerResult?: any; // Backbone.Model - only for newsroom
  context?: string; // 'agents' or 'newsroom'
}

function handleDownloadClick(agentResult: any) {
    const agentResultDownload = new AgentResultDownload({ agentResult });
    agentResultDownload.downloadPDF();
    Analytics.sendEvent("br_agent_result_downloaded", {
      kind: agentResult.get("kind"),
    });
  }

export function renderArticleTitle(
  clipUrl: string,
  languages: string[],
  title: string,
  handleDownloadClick?: (agentResult: any) => void,
  agentResult?: any,
) {
  const isPdfUrl = clipUrl && clipUrl.endsWith('.pdf')

  const handleTitleClick = (e: React.MouseEvent) => {
    if (isPdfUrl) {
      e.preventDefault();
      handleDownloadClick?.(agentResult);
    }
  };

  if (clipUrl) {
    if (languages.length > 0) {
      return (
        <a
          href={clipUrl}
          className="clip-detail__title hyphenate"
          lang={languages[0]}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => handleTitleClick(e)}
        >
          {title}
        </a>
      );
    } else {
      return (
        <a
          href={clipUrl}
          className="clip-detail__title"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => handleTitleClick(e)}
        >
          {title}
        </a>
      );
    }
  } else {
    if (languages.length > 0) {
      return (
        <span className="clip-detail__title hyphenate" lang={languages[0]}>
          {title}
        </span>
      );
    } else {
      return <span className="clip-detail__title">{title}</span>;
    }
  }
}

export default function DatalakeDetailView({
  selectedAgentResults,
  agentResult,
  readerResult,
  context,
}: Props) {
  const clipId = agentResult.get("clip_id");
  const agentResultId = agentResult.get("id");
  const sourceInfo = agentResult.get("primary_source");
  const publicationTime = agentResult.get("publish_time");
  const aggregationTime = agentResult.get("created_at");
  const title = agentResult.get("title");
  const languages = agentResult.get("languages") || [];
  const text = agentResult.get("text");
  const richText = agentResult.richText ? agentResult.richText() : agentResult.get("rich_text");
  const important = agentResult.get("important");
  const ratingValue = agentResult.get("rating_value");
  const isDatalakeClip = agentResult.isDataLakeClip();
  const highlightedText = richText ? highlightRichText(richText) : highlight(text || "");

  useEffect(() => {
    // Shortcut: 'o' opens primary source URL
    const openPrimarySource = () => {
      const href = agentResult.get("primary_source")?.clip_url;
      if (href) {window.open(href, "_blank");}
    };
    Shortcutter.bindInModule("agents", "o", openPrimarySource);

    // Shortcut: 'alt+o' opens admin clip URL
    const openAdminClip = () => {
      if (session.isImpersonated() || session.can("internal_access")) {
        window.open(adminClipUrl(clipId), "_blank");
      }
    };
    Shortcutter.bindInModule("agents", "alt+o", openAdminClip);

    // Cleanup shortcuts on unmount
    return () => {
      Shortcutter.unbindInModule("agents", "o");
      Shortcutter.unbindInModule("agents", "alt+o");
    };
  }, [agentResult, clipId]);

  return (
    <div data-datalake-view>
      <div className="lightened-container lifted-container pvm phm">
        <div className="link-bar mbs" style={{ paddingTop: 0 }}>
          <DatalakeHeaderActions agentResult={agentResult} context={context} readerResult={readerResult}/>
          <div className="mts">
            <ShowClipIdHeader clipId={clipId} agentResultId={agentResultId} isDatalakeClip={isDatalakeClip} />
          </div>
        </div>
        <DatalakeClipPublicationInfo
          sourceInfo={sourceInfo}
          publicationTime={publicationTime}
          aggregationTime={aggregationTime}
        />
        <div className="clip-detail__head">
          <h1 className="heading-xlarge">
            {renderArticleTitle(sourceInfo?.clip_url, languages, title, handleDownloadClick, agentResult)}
          </h1>
        </div>
        <div className="clip-detail__body pbm">
          <DatalakeGalleryCarousel agentResult={agentResult} />
          {richText ? (
            <div className="hyphenate" dangerouslySetInnerHTML={{ __html: highlightedText }} />
          ) : (
            <p className="hyphenate" dangerouslySetInnerHTML={{ __html: highlightedText }} />
          )}
        </div>
        <DatalakeMetadataView agentResult={agentResult} />
        <hr className="separator mvl" />
        <DetailViewFooter
          important={important}
          selectedAgentResults={selectedAgentResults}
          ratingValue={ratingValue}
          agentResult={agentResult}
          context={context}
        />
      </div>
    </div>
  );
}
